import type { FC, Ref } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import { Autocomplete, Box, Stack, TextField } from "@mui/material";
import { selectPartnerOptionsQueryOptions, movePartnerConnectedEntitiesMutationOptions } from "../../api/partners";
import type { GetSelectPartnerOptionsResponse, MovePartnerConnectedEntitiesPayload } from "../../api/partners";
import * as FormDialog from "../../shared/FormDialog";
import type { AutocompleteOptionType } from "../../shared/Autocomplete";
import { AUTOCOMPLETE_LOADING_OPTION, AutocompleteOption } from "../../shared/Autocomplete";
import { PartnerIcon3 } from "../../shared/icons";
import { ReplaceDialog } from "../../shared/ReplaceDialog";
import { useMutation, useQuery } from "@tanstack/react-query";

type ReplacePartnerDialogProps = {
  onClose: () => void;
  partnerId: string;
  partnerName: string;
  connections: MovePartnerConnectedEntitiesPayload["connections"];
};

type PartnerOption = GetSelectPartnerOptionsResponse[number];

export const ReplacePartnerDialog: FC<ReplacePartnerDialogProps> = ({
  onClose,
  partnerId,
  partnerName,
  connections,
}) => {
  const movePartnerConnectedEntitiesMutation = useMutation(movePartnerConnectedEntitiesMutationOptions(partnerId));

  return (
    <ReplaceDialog<PartnerSelectOption>
      onClose={onClose}
      selectStep={{
        title: "Partner.Replace.DialogHeader",
        fromField: {
          label: "Partner.Replace.CurrentPartnerLabel",
          icon: <PartnerIcon3 />,
          value: partnerName,
        },
        targetField: {
          label: "Partner.Replace.ReplaceWithLabel",
          renderSelect: ({ value, onChange, ref, isRequiredError }) => (
            <PartnerSelect
              value={value}
              onChange={onChange}
              inputRef={ref}
              isRequiredError={isRequiredError}
              currentPartnerId={partnerId}
            />
          ),
        },
        continueLabel: "Partner.Replace.ContinueButton",
      }}
      confirmStep={{
        renderDescription: ({ target }) => (
          <Translate
            translationKey={
              connections.length === 1
                ? "Partner.Replace.ConfirmMessage.Singular"
                : "Partner.Replace.ConfirmMessage.Plural"
            }
            values={{
              name: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {partnerName}
                </Box>
              ),
              targetName: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {target?.name}
                </Box>
              ),
              count: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {connections.length}
                </Box>
              ),
            }}
          />
        ),
        onConfirm: target => movePartnerConnectedEntitiesMutation.mutateAsync({ newPartnerId: target.id, connections }),
        confirmLabel: "Partner.Replace.ConfirmButton",
      }}
    />
  );
};

type PartnerSelectOption = Omit<PartnerOption, "roles">;

type PartnerSelectProps = {
  currentPartnerId: string;
  value: PartnerSelectOption | null;
  onChange: (partner: PartnerSelectOption) => void;
  inputRef: Ref<unknown>;
  isRequiredError: boolean;
};

const PartnerSelect = ({ currentPartnerId, value, onChange, inputRef, isRequiredError }: PartnerSelectProps) => {
  const { t } = useTranslation();
  const partnersQuery = useQuery(selectPartnerOptionsQueryOptions());

  const selectablePartners = partnersQuery.data?.filter(({ id }) => id !== currentPartnerId);
  const options: AutocompleteOptionType[] = selectablePartners
    ? selectablePartners.map(partner => ({ label: partner.name, value: partner.id, data: partner }))
    : [AUTOCOMPLETE_LOADING_OPTION];

  return (
    <Stack sx={{ position: "relative" }}>
      <FormDialog.Field errorMessage={isRequiredError && t("Partner.Replace.RequiredValidation")}>
        <Autocomplete
          noOptionsText={t("Partner.Replace.SelectPartnerEmpty")}
          options={options}
          clearIcon={null}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, selected) => option.value === selected?.value}
          value={value ? { label: value.name, value: value.id } : null}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return <AutocompleteOption key={option.value} option={option} {...optionProps} />;
          }}
          onChange={(_, option) => option && onChange({ id: option.value, name: option.label })}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={t("Partner.Replace.SelectPartnerPlaceholder")}
              size="small"
              inputRef={inputRef}
              slotProps={{
                ...params.slotProps,
                input: { ...params.slotProps.input, startAdornment: value ? <PartnerIcon3 /> : null },
              }}
            />
          )}
        />
      </FormDialog.Field>
    </Stack>
  );
};
